import { workspaceTextFor } from './interface-copy';
// Collaboration v1, PR 6: living Results, "Made from · Used by", the company
// feed and the template gallery (collaboration-routes.mjs).
import { companyRequest } from '@/company/api';
import type { CompanyResult, CompanySkill } from './types';

const root = '/api/v1/company';
const segment = (id: string) => encodeURIComponent(id);
export type GalleryLocale =
  | 'en'
  | 'bg'
  | 'hr'
  | 'cs'
  | 'da'
  | 'nl'
  | 'et'
  | 'fi'
  | 'fr'
  | 'de'
  | 'el'
  | 'hu'
  | 'ga'
  | 'it'
  | 'lv'
  | 'lt'
  | 'mt'
  | 'pl'
  | 'pt'
  | 'ro'
  | 'sk'
  | 'sl'
  | 'es'
  | 'sv';

export interface ResultRevisionEntry {
  revision: number;
  title: string;
  authorKind: 'person' | 'assistant' | 'agent';
  author: { userId: string; name: string | null } | null;
  /** The outside agent an External MCP save came from ("Claude"), else null. */
  clientName?: string | null;
  note: string | null;
  createdAt: string;
  origin: {
    threadId: string | null;
    agentRunId?: string;
    agentId?: string;
    agentName?: string | null;
  };
}
export interface ResultHistory {
  currentRevision: number;
  canEdit: boolean;
  revisions: ResultRevisionEntry[];
}
export interface ResultSharingCheck {
  /** `own`: the caller's connection (with its account). Otherwise a colleague's,
   * named only by whose it was and its app. */
  privateConnections: {
    own: boolean;
    connectionId: string | null;
    name: string | null;
    accountLabel: string | null;
    toolName: string | null;
    personName: string | null;
    /** App code in the Result's chats read it, not only the chat. */
    appRead: boolean;
  }[];
  /** A personal agent's name is shown to its owner only. */
  maintainedByPersonalAgent: { id: string | null; name: string | null; own: boolean } | null;
  warn: boolean;
}
export type LinkItemKind =
  | 'result'
  | 'skill'
  | 'meeting'
  | 'agent'
  | 'chat'
  | 'file'
  | 'source'
  | 'knowledge_page';
export interface LinkEnd {
  kind: LinkItemKind;
  id: string;
  title: string;
  href: string;
  relation: string;
  version: number | null;
  ownerUserId: string | null;
  ownerName: string | null;
}
export interface ItemLinks {
  item: LinkEnd;
  madeFrom: LinkEnd[];
  usedBy: LinkEnd[];
  usage: { answers: number; sinceDays: number } | null;
}
export interface FeedRow {
  kind: LinkItemKind;
  id: string;
  title: string;
  verb: 'shared' | 'updated' | 'published' | 'approved' | 'added' | 'ran';
  version?: number;
  actor: { userId: string; name: string | null } | null;
  at: string;
  href: string;
  action: 'open' | 'use' | 'run' | 'fork';
}
export interface CompanyFeed {
  since: string;
  rows: FeedRow[];
}
export interface GalleryTemplate {
  id: string;
  name: string;
  description: string;
  draftInput: {
    name: string;
    description: string;
    instructions: string;
    modelChoice?: import('./model-choices.generated').ModelChoice;
    fileIds: string[];
    sourceIds: string[];
  };
  needs: { label: string; connected: boolean }[];
  ready: boolean;
  missing?: string[];
  suggestedSchedule: { cron: string; timezone: string; label: string } | null;
}

// A malformed answer reads as empty rather than breaking the view that asked.
const list = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);
function linksOf(value: Partial<ItemLinks> | null | undefined): ItemLinks {
  const usage = value?.usage;
  return {
    item: value?.item as LinkEnd,
    madeFrom: list<LinkEnd>(value?.madeFrom),
    usedBy: list<LinkEnd>(value?.usedBy),
    usage: usage && Number.isInteger(usage.answers) ? usage : null,
  };
}

export const livingApi = {
  editResult: (id: string, input: { baseRevision: number; payload: unknown; note?: string }) =>
    companyRequest<{ revision: number; result: CompanyResult }>(
      `${root}/results/${segment(id)}`,
      'PATCH',
      input,
    ),
  history: (id: string) =>
    companyRequest<Partial<ResultHistory>>(`${root}/results/${segment(id)}/revisions`).then(
      (value): ResultHistory => ({
        currentRevision: Number(value?.currentRevision ?? 0),
        canEdit: value?.canEdit === true,
        revisions: list<ResultRevisionEntry>(value?.revisions),
      }),
    ),
  restore: (id: string, revision: number, baseRevision: number) =>
    companyRequest<{ revision: number; result: CompanyResult }>(
      `${root}/results/${segment(id)}/revisions/${revision}/restore`,
      'POST',
      { baseRevision },
    ),
  sharingCheck: (id: string) =>
    companyRequest<ResultSharingCheck>(`${root}/results/${segment(id)}/sharing-check`),
  links: (kind: LinkItemKind, id: string) =>
    companyRequest<Partial<ItemLinks>>(`${root}/items/${kind}/${segment(id)}/links`).then(linksOf),
  feed: () =>
    companyRequest<Partial<CompanyFeed>>(`${root}/feed?since=7d`).then(
      (value): CompanyFeed => ({
        since: String(value?.since ?? '7d'),
        rows: list(value?.rows),
      }),
    ),
  gallery: (locale: GalleryLocale) =>
    companyRequest<{ templates?: GalleryTemplate[] }>(`${root}/gallery?locale=${locale}`).then(
      (value) => ({ templates: list<GalleryTemplate>(value?.templates) }),
    ),
  useTemplate: (template: GalleryTemplate) =>
    companyRequest<{ skill: CompanySkill }>(`${root}/skills`, 'POST', {
      ...template.draftInput,
      templateId: template.id,
    }),
  duplicateResult: (result: CompanyResult, title: string) =>
    companyRequest<{ result: CompanyResult }>(`${root}/results`, 'POST', {
      title,
      kind: result.kind,
      payload: result.payload,
      sourceIds: result.sourceIds,
    }),
};

// "2 h ago", "Yesterday", "3 days ago", then the date. Shared by the Result
// eyebrow, History and the feed.
export function relativeTime(value: string | null | undefined, now = Date.now(), locale = 'en-GB') {
  if (!value) return '';
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return '';
  const minutes = Math.max(0, Math.floor((now - time) / 60000));
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (minutes < 1) return locale === 'en-GB' ? 'just now' : format.format(0, 'minute');
  if (minutes < 60) return format.format(-minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (hours < 24) return format.format(-hours, 'hour');
  const days = Math.round(hours / 24);
  if (days === 1) return locale === 'en-GB' ? 'yesterday' : format.format(-days, 'day');
  if (days < 7) return format.format(-days, 'day');
  return new Date(time).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

/** Feed rows grouped for "New at {company} this week": Today, then the rest. */
export function groupFeedRows(rows: FeedRow[], now = new Date(), locale = 'en') {
  const text = workspaceTextFor(locale);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const groups: { label: string; rows: FeedRow[] }[] = [];
  for (const row of rows) {
    const label = text(Date.parse(row.at) >= today ? 'Today' : 'Earlier this week');
    const group = groups.find((entry) => entry.label === label);
    if (group) group.rows.push(row);
    else groups.push({ label, rows: [row] });
  }
  return groups;
}
